/**
 * Offline Action Queue
 * Stores actions (workout completions, feedback) when offline
 * and syncs them when the connection is restored.
 */

const QUEUE_KEY = "offline_action_queue";

export interface OfflineAction {
  id: string;
  type: "complete_workout" | "workout_feedback";
  payload: Record<string, unknown>;
  createdAt: number;
}

export function getOfflineQueue(): OfflineAction[] {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
  } catch {
    return [];
  }
}

export function addToOfflineQueue(action: Omit<OfflineAction, "id" | "createdAt">): void {
  const queue = getOfflineQueue();
  queue.push({
    ...action,
    id: crypto.randomUUID(),
    createdAt: Date.now(),
  });
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
}

export function removeFromOfflineQueue(id: string): void {
  const queue = getOfflineQueue().filter((a) => a.id !== id);
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
}

export function clearOfflineQueue(): void {
  localStorage.removeItem(QUEUE_KEY);
}

/**
 * Cache individual workout details for offline access
 */
const WORKOUT_CACHE_KEY = "offline_workouts_cache";

export function cacheWorkoutsOffline(workouts: any[]): void {
  try {
    localStorage.setItem(WORKOUT_CACHE_KEY, JSON.stringify(workouts));
  } catch {
    // Storage full — ignore
  }
}

export function getCachedWorkouts(): any[] {
  try {
    return JSON.parse(localStorage.getItem(WORKOUT_CACHE_KEY) || "[]");
  } catch {
    return [];
  }
}
